
import {
    createToolbarEvent,
    sendAnalytics
} from '../../../analytics';
import { openDialog } from '../../../base/dialog';
import { IconRec } from '../../../base/icons';
import { isLocalParticipantModerator } from '../../../base/participants';
import { AbstractButton, type AbstractButtonProps } from '../../../base/toolbox/components';
import { isDevelopment } from '../../../base/util';

import { StartLocalRecordingDialog, StopLocalRecordingDialog } from './_';

/**
 * The type of the React {@code Component} props of
 * {@link AbstractLocalRecordButton}.
 */
export type Props = AbstractButtonProps & {

    /**
     * True if the button needs to be disabled.
     */
    _disabled: Boolean,

    /**
     * True if there is a running local recording, false otherwise.
     */
    _isLocalRecordingRunning: boolean,

    /**
     * The tooltip to display when hovering over the button.
     */
    _tooltip: ?String,

    /**
     * The redux {@code dispatch} function.
     */
    dispatch: Function,

    /**
     * The i18n translate function.
     */
    t: Function
};

/**
 * An abstract implementation of a button for starting local recording.
 */
export default class AbstractLocalRecordButton<P: Props> extends AbstractButton<P, *> {
    accessibilityLabel = 'toolbar.accessibilityLabel.localRecording';
    icon = IconRec;

    /**
     * Constructor.
     *
     * @param {*} props - Props of the class.
     */
    constructor(props: P) {
        super(props);

        this.label = 'localRecordingDialog.dialogTitle';
        this.toggledLabel = 'localRecordingDialog.stop';
    }

    /**
     * Returns the tooltip that should be displayed when the button is disabled.
     *
     * @private
     * @returns {string}
     */
    _getTooltip() {
        return this.props._tooltip || '';
    }

    /**
     * Handles clicking / pressing the button.
     *
     * @override
     * @protected
     * @returns {void}
     */
    _handleClick() {
        const { _isLocalRecordingRunning, dispatch } = this.props;

        sendAnalytics(createToolbarEvent(
            'local.recording.button',
            {
                'is_recording': _isLocalRecordingRunning
            }));

        if (_isLocalRecordingRunning) {
            // Open stop confirmation dialog
            dispatch(openDialog(StopLocalRecordingDialog));
        } else {
            // Open dialog to start recording
            dispatch(openDialog(StartLocalRecordingDialog));
        }
    }

    /**
     * Helper function to be implemented by subclasses, which must return a
     * boolean value indicating if this button is disabled or not.
     *
     * @override
     * @protected
     * @returns {boolean}
     */
    _isDisabled() {
        return this.props._disabled;
    }

    /**
     * Indicates whether this button is in toggled state or not.
     *
     * @override
     * @protected
     * @returns {boolean}
     */
    _isToggled() {
        return this.props._isLocalRecordingRunning;
    }
}

/**
 * Maps (parts of) the redux state to the associated props for the
 * {@code LocalRecordButton} component.
 *
 * @param {Object} state - The Redux state.
 * @param {Props} ownProps - The own props of the Component.
 * @private
 * @returns {{
 *     _disabled: boolean,
 *     _isLocalRecordingRunning: boolean,
 *     visible: boolean
 * }}
 */
export function _mapStateToProps(state: Object, ownProps: Props): Object {
    let { visible } = ownProps;
    const isModerator = isLocalParticipantModerator(state);

    // Import here to avoid circular dependency
    const LocalRecordingManager = require('../Recording/LocalRecordingManager.web').default;
    const { localRecording } = state['features/base/config'];

    // In development mode, always show the button for testing (unless explicitly disabled in config)
    const isEnabledInConfig = isDevelopment()
        ? !(localRecording && localRecording.disable === true)
        : localRecording && localRecording.disable !== true;

    if (typeof visible === 'undefined') {
        // Only show if:
        // 1. Local recording is enabled in config (or in dev mode)
        // 2. User is moderator
        // 3. Browser supports local recording
        visible = isEnabledInConfig && isModerator && LocalRecordingManager.isSupported();
    }

    const _disabled = !LocalRecordingManager.isSupported();
    const _tooltip = _disabled ? 'localRecordingDialog.notSupported' : '';

    return {
        _disabled,
        _isLocalRecordingRunning: LocalRecordingManager.isRecordingLocally(),
        _tooltip,
        visible
    };
}
